import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import { Card, CardContent, CardHeader, CardTitle, UploadDropzone } from '@xxtract/ui';
import { useCreateIntakeSession } from '../hooks/useIntake';
import { KiesEersteKlant } from '../components/KiesEersteKlant';
import { useZonderGekozenKlant } from '../hooks/useZonderGekozenKlant';
import type { IntakeSessionFlow } from '../intake.api';

/**
 * h7-2: marketing staat er bewust NIET in. Er is nog geen marketingsjabloon in
 * `public/templates/`, en een link naar een bestand dat niet bestaat levert een 404 op waar de
 * klant niets mee kan. Zolang het sjabloon ontbreekt, tonen we de link voor deze flow niet.
 */
const TEMPLATE_LINKS: Partial<Record<IntakeSessionFlow, string>> = {
  product: '/templates/intake-product-template.xlsx',
  packaging: '/templates/intake-packaging-template.xlsx',
};

const FLOW_LABEL_KEYS: Record<IntakeSessionFlow, string> = {
  product: 'intake.upload.flowProduct',
  packaging: 'intake.upload.flowPackaging',
  marketing: 'intake.upload.flowMarketing',
};

function resolveUploadErrorKey(error: unknown): string {
  const axiosError = error as AxiosError<{ error?: { code?: string } }>;
  const code = axiosError?.response?.data?.error?.code;
  if (code && code in KNOWN_ERROR_CODES) return `intake.upload.errors.${code}`;
  return 'intake.upload.errors.generic';
}

// Afgeleid en niet opnieuw opgeschreven: een vierde flow zou anders stil ontbreken op het scherm.
const FLOW_OPTIONS = Object.keys(FLOW_LABEL_KEYS) as IntakeSessionFlow[];

const KNOWN_ERROR_CODES: Record<string, true> = {
  INTAKE_XLS_NOT_SUPPORTED: true,
  INTAKE_INVALID_FILE_TYPE: true,
  INTAKE_EMPTY_FILE: true,
  INTAKE_FILE_TOO_LARGE: true,
  INTAKE_CORRUPT_FILE: true,
  INTAKE_ROW_LIMIT_EXCEEDED: true,
};

export function IntakeUploadPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [flow, setFlow] = useState<IntakeSessionFlow>('product');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const createSession = useCreateIntakeSession();
  const zonderKlant = useZonderGekozenKlant();

  const handleFilesAccepted = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setErrorKey(null);
    createSession.mutate(
      { flow, file },
      {
        onSuccess: (session) => navigate(`/intake/${session.id}`),
        onError: (error) => setErrorKey(resolveUploadErrorKey(error)),
      },
    );
  };

  if (zonderKlant) {
    return <KiesEersteKlant />;
  }

  return (
    <section data-testid="intake-upload-page" className="max-w-2xl">
      <h1 className="mb-5 text-xl font-bold text-[var(--foreground-heading)]">{t('intake.upload.title')}</h1>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>{t('intake.upload.flowLabel')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div role="radiogroup" aria-label={t('intake.upload.flowLabel')} className="flex gap-2">
            {FLOW_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={flow === option}
                data-testid={`intake-flow-${option}`}
                onClick={() => setFlow(option)}
                className={`rounded-[var(--radius-control)] border px-4 py-2 text-sm font-medium ${
                  flow === option
                    ? 'border-transparent bg-[var(--primary)] text-white'
                    : 'border-[var(--border)] bg-[var(--background)] text-[var(--foreground)]'
                }`}
              >
                {t(FLOW_LABEL_KEYS[option])}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <UploadDropzone
        activeStep={1}
        disabled={createSession.isPending}
        labels={{
          dropzoneLabel: t('intake.upload.dropzoneLabel'),
          instruction: createSession.isPending ? t('intake.upload.submitting') : t('intake.upload.instruction'),
          browseInstruction: t('intake.upload.browseInstruction'),
          invalidType: t('intake.upload.errors.invalidType'),
          fileTooLarge: t('intake.upload.errors.fileTooLarge'),
          emptyFile: t('intake.upload.errors.emptyFile'),
          multipleNotAllowed: t('intake.upload.errors.multipleNotAllowed'),
          preCheckFailed: t('intake.upload.errors.preCheckFailed'),
          steps: t('intake.upload.steps', { returnObjects: true }) as [string, string, string, string],
        }}
        onFilesAccepted={handleFilesAccepted}
      />

      {errorKey ? (
        <p role="alert" data-testid="intake-upload-server-error" className="mt-2 text-sm text-[var(--destructive)]">
          {t(errorKey)}
        </p>
      ) : null}

      {TEMPLATE_LINKS[flow] ? (
        <p className="mt-4 text-sm text-[var(--foreground-muted)]">
          {t('intake.upload.templateLinkLabel')}{' '}
          <a className="text-[var(--secondary)] hover:underline" href={TEMPLATE_LINKS[flow]} download>
            {t('intake.upload.templateLinkCta')}
          </a>
        </p>
      ) : null}
    </section>
  );
}
